import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { subscriptionApi } from '../../services/subscription.service';
import { SubscriptionPlan, Subscription, SubscriptionProrationPreview } from '../../types/subscription';

const currencySymbols: Record<string, string> = {
  INR: 'Rs.',
  USD: '$',
};

const planAccents: Record<string, string> = {
  FREE: 'from-slate-200 via-slate-100 to-white',
  BASIC: 'from-sky-200 via-cyan-100 to-white',
  PREMIUM: 'from-amber-200 via-orange-100 to-white',
  ENTERPRISE: 'from-emerald-200 via-teal-100 to-white',
};

const StudentSubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [switchingPlanId, setSwitchingPlanId] = useState<string | null>(null);

  const loadData = useCallback(async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        const [plansResult, subscriptionResult] = await Promise.allSettled([
          subscriptionApi.getAllPlans('STUDENT'),
          subscriptionApi.getCurrentSubscription(),
        ]);

        if (plansResult.status === 'fulfilled') {
          setPlans(plansResult.value);
        } else {
          throw plansResult.reason;
        }

        if (subscriptionResult.status === 'fulfilled') {
          setCurrentSubscription(subscriptionResult.value.currentSubscription || null);
        } else if ((subscriptionResult.reason as any)?.response?.status === 404) {
          setCurrentSubscription(null);
        } else {
          throw subscriptionResult.reason;
        }

        setError(null);
        setLastUpdatedAt(new Date());
      } catch (err) {
        console.error('Failed to load subscription plans:', err);
        setError('Failed to load plans.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
  }, []);

  useEffect(() => {
    void loadData(false);
    const intervalId = window.setInterval(() => {
      void loadData(true);
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [loadData]);

  const sortedPlans = useMemo(() => {
    const order = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'];
    return [...plans].sort((a, b) => {
      const sortDelta = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
      if (sortDelta !== 0) return sortDelta;
      return order.indexOf(a.planType) - order.indexOf(b.planType);
    });
  }, [plans]);

  const onSelectPlan = async (plan: SubscriptionPlan) => {
    if (currentSubscription?.planId === plan.id) return;

    let prorationPreview: SubscriptionProrationPreview | undefined;
    if (currentSubscription) {
      try {
        setSwitchingPlanId(plan.id);
        prorationPreview = await subscriptionApi.previewPlanSwitch(plan.id);
      } catch (err: any) {
        const message = err?.response?.data?.message || 'Failed to calculate prorated switch amount.';
        alert(message);
        return;
      } finally {
        setSwitchingPlanId(null);
      }
    }

    navigate('/subscription/payment', {
      state: {
        planId: plan.id,
        planName: plan.name,
        amount: prorationPreview ? prorationPreview.amountDue : plan.price,
        originalAmount: plan.price,
        currency: plan.currency || 'INR',
        billingCycle: plan.billingCycle || 'monthly',
        mode: currentSubscription ? 'upgrade' : 'new',
        prorationPreview,
      },
    });
  };

  if (loading) {
    return <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 text-sm text-slate-600">Loading subscription plans...</div>;
  }

  if (error) {
    return <div className="rounded-[1.75rem] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Student plans</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Live plan selection</h2>
            <p className="mt-1 text-sm text-slate-500">
              Plans are loaded from the backend and refreshed automatically.
              {lastUpdatedAt ? ` Last updated ${lastUpdatedAt.toLocaleTimeString()}.` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadData(true)}
            disabled={refreshing}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowPathIcon className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-4">
        {sortedPlans.map((plan) => {
          const isCurrentPlan = currentSubscription?.planId === plan.id;
          const isPreparing = switchingPlanId === plan.id;
          const symbol = currencySymbols[plan.currency || 'INR'] || (plan.currency || 'INR');
          const accent = planAccents[plan.planType] || 'from-slate-200 via-slate-100 to-white';
          const features = [
            `${plan.features.maxExamAttempts ?? 0} exam attempts`,
            `${plan.features.examResultsAccess ? 'Detailed' : 'Basic'} results`,
            `${plan.features.certificateGeneration ? 'Certificates included' : 'No certificates'}`,
            `${plan.features.hasAnalytics ? 'Analytics enabled' : 'Analytics not included'}`,
          ];

          return (
            <article
              key={plan.id}
              className={`relative overflow-hidden rounded-[2rem] border bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.32)] transition hover:-translate-y-1 hover:shadow-[0_30px_90px_-46px_rgba(15,23,42,0.4)] ${
                isCurrentPlan ? 'border-emerald-300' : 'border-slate-200'
              }`}
            >
              <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${accent}`} />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{plan.planType}</p>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{plan.name}</h2>
                  </div>
                  {isCurrentPlan ? (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Current
                    </span>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                      {plan.billingCycle}
                    </span>
                  )}
                </div>

                <div className="mt-8">
                  <p className="text-4xl font-semibold tracking-tight text-slate-950">
                    {symbol}
                    {Number(plan.price || 0).toFixed(2)}
                  </p>
                  <p className="mt-2 text-sm text-slate-500">per {plan.billingCycle} plan</p>
                </div>

                <p className="mt-5 min-h-[72px] text-sm leading-6 text-slate-600">
                  {plan.description || 'Subscription details are available during selection.'}
                </p>

                <div className="mt-5 space-y-3">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={isCurrentPlan || isPreparing}
                  onClick={() => void onSelectPlan(plan)}
                  className={`mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition ${
                    isCurrentPlan
                      ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                      : 'bg-slate-950 text-white hover:bg-slate-800'
                  }`}
                >
                  {isCurrentPlan ? 'Selected plan' : isPreparing ? 'Calculating...' : 'Choose plan'}
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="rounded-[1.75rem] border border-sky-200 bg-sky-50 p-5">
        <div className="flex items-start gap-3">
          <SparklesIcon className="h-6 w-6 text-sky-700" />
          <div>
            <p className="text-sm font-semibold text-sky-900">Simple plan selection</p>
            <p className="mt-1 text-sm leading-6 text-sky-800">
              Pick the plan that fits your exam usage and analytics needs. Your current plan stays clearly marked so upgrades are obvious.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentSubscriptionPlans;
